import axios from 'axios';
import { getApiErrorMessage } from './errorUtils';
import { PRINT_ACK_TIMEOUT_MS } from '../utils/printAckLoop';
import type {
  ShelfLocation,
  ShelfItem,
  LabelStatus,
  LabelLookup,
  BrowseIndexesResponse,
  BrowseCheckedOutIndexesResponse,
  ApiMessageResult,
  CheckOutByShelfResponse,
  AssignBatchResponse,
} from '../schemas/apiResponse.schemas';
import {
  ShelfLocationSchema,
  ShelfItemSchema,
  LabelStatusSchema,
  LabelLookupSchema,
  BrowseIndexesResponseSchema,
  BrowseCheckedOutIndexesResponseSchema,
  CheckOutByShelfResponseSchema,
  AssignBatchResponseSchema,
} from '../schemas/apiResponse.schemas';
import { validateResponse } from './validateResponse';
import type { LabelType } from '@/shared/types/label.types';

async function fetchStorageIdPrefixList(
  path: 'storage-id-prefixes' | 'storage-id-prefixes/checked-out'
): Promise<string[]> {
  try {
    const response = await axios.get(`/api/supabase/shelf-index/${path}`);
    if (response.status === 200 && response.data?.prefixes) {
      return response.data.prefixes;
    }
    return [];
  } catch (error: unknown) {
    console.error(
      path === 'storage-id-prefixes/checked-out'
        ? 'Error fetching checked-out storage ID prefixes:'
        : 'Error fetching storage ID prefixes:',
      error
    );
    return [];
  }
}

export const getStorageIdPrefixes = async (): Promise<string[]> => {
  return fetchStorageIdPrefixList('storage-id-prefixes');
};

export const getCheckedOutStorageIdPrefixes = async (): Promise<string[]> => {
  return fetchStorageIdPrefixList('storage-id-prefixes/checked-out');
};

/** Get SKU locations from shelf_indexes (checked_in = true). Returns { shelf_index, type, id } per row. */
export const searchSkuLocations = async (sku: string): Promise<{ locations: ShelfLocation[]; error?: string }> => {
  try {
    const trimmed = String(sku || '').trim();
    if (!trimmed) return { locations: [] };
    const response = await axios.get('/api/supabase/shelf-index/sku-locations', {
      params: { sku: trimmed }
    });
    if (response.status === 200 && response.data?.locations) {
      return { locations: validateResponse(ShelfLocationSchema.array(), response.data.locations, 'searchSkuLocations') };
    }
    return { locations: [] };
  } catch (error: unknown) {
    console.error('Error searching SKU locations:', error);
    return {
      locations: [],
      error: getApiErrorMessage(error, 'Failed to search SKU locations')
    };
  }
};

/** Get items on a shelf from shelf_indexes (checked_in = true). Returns { sku, type, id, shelf_index } per row. */
export const searchShelfItems = async (shelfIndex: string): Promise<{ items: ShelfItem[]; error?: string }> => {
  try {
    const trimmed = String(shelfIndex || '').trim();
    if (!trimmed) return { items: [] };
    const response = await axios.get('/api/supabase/shelf-index/shelf-items', {
      params: { shelf: trimmed }
    });
    if (response.status === 200 && response.data?.items) {
      return { items: validateResponse(ShelfItemSchema.array(), response.data.items, 'searchShelfItems') };
    }
    return { items: [] };
  } catch (error: unknown) {
    console.error('Error searching shelf items:', error);
    return {
      items: [],
      error: getApiErrorMessage(error, 'Failed to search shelf items')
    };
  }
};

/** Get shelf index history by 6-char storage_id prefix. Returns { shelf_index, created_at }[] ordered by date desc. */
export const getShelfIndexHistory = async (storageIdPrefix: string): Promise<Array<{ shelf_index: string | null; created_at: string }>> => {
  try {
    const prefix = String(storageIdPrefix || '').trim().toLowerCase();
    if (prefix.length !== 6) return [];
    const response = await axios.get('/api/supabase/shelf-index/shelf-index-history', {
      params: { prefix }
    });
    if (response.status === 200 && Array.isArray(response.data?.history)) {
      return response.data.history;
    }
    return [];
  } catch (error: unknown) {
    console.error('Error fetching shelf index history:', error);
    return [];
  }
};

/** Get label status by 6-char storage_id prefix. Returns { found, checkedIn } */
export const getLabelStatus = async (storageIdPrefix: string): Promise<LabelStatus> => {
  try {
    const prefix = String(storageIdPrefix || '').trim().toLowerCase();
    if (prefix.length !== 6) return { found: false, checkedIn: false };
    const response = await axios.get('/api/supabase/shelf-index/label-status', {
      params: { prefix }
    });
    return validateResponse(LabelStatusSchema, response.data, 'getLabelStatus');
  } catch (error: unknown) {
    console.error('Error fetching label status:', error);
    return { found: false, checkedIn: false };
  }
};

type CheckLabelResult = { success: boolean; message: string };

async function postShelfIndexCheck(
  path: 'check-out' | 'check-in',
  storageIdPrefix: string,
  failMessage: string
): Promise<CheckLabelResult> {
  try {
    const response = await axios.post(`/api/supabase/shelf-index/${path}`, {
      storageIdPrefix: String(storageIdPrefix).trim().toLowerCase()
    });
    if (response.status === 200 && response.data?.success) {
      return { success: true, message: response.data.message };
    }
    return {
      success: false,
      message: response.data?.error || failMessage
    };
  } catch (error: unknown) {
    console.error(`Error ${path} label:`, error);
    return {
      success: false,
      message: getApiErrorMessage(error, failMessage)
    };
  }
}

export const checkOutLabel = async (storageIdPrefix: string): Promise<CheckLabelResult> => {
  return postShelfIndexCheck('check-out', storageIdPrefix, 'Failed to check out label');
};

/** Check in a label (set checked_in = true) */
export const checkInLabel = async (storageIdPrefix: string): Promise<CheckLabelResult> => {
  return postShelfIndexCheck('check-in', storageIdPrefix, 'Failed to check in label');
};

/** Check out every checked-in label currently on a shelf (stock + pick bin). */
export const checkOutShelfLabels = async (shelfIndex: string): Promise<CheckOutByShelfResponse> => {
  const response = await axios.post('/api/supabase/shelf-index/check-out-by-shelf', {
    shelfIndex: String(shelfIndex || '').trim(),
  });
  return validateResponse(
    CheckOutByShelfResponseSchema,
    response.data,
    'POST /api/supabase/shelf-index/check-out-by-shelf'
  );
};

/** Assign storage-id prefixes to a shelf and check them in (audit rescan confirm). */
export const assignBatchToShelf = async (
  shelfIndex: string,
  storageIdPrefixes: string[]
): Promise<AssignBatchResponse> => {
  const response = await axios.post('/api/supabase/shelf-index/assign-batch', {
    shelfIndex: String(shelfIndex || '').trim(),
    storageIdPrefixes,
  });
  return validateResponse(
    AssignBatchResponseSchema,
    response.data,
    'POST /api/supabase/shelf-index/assign-batch'
  );
};

/** Mark long boxes for prefixes that stay on the shelf. Does not check out or reassign. */
export const markLongBoxesWithoutReassign = async (
  shelfIndex: string,
  storageIdPrefixes: string[],
): Promise<AssignBatchResponse> => {
  const response = await axios.post('/api/supabase/shelf-index/long-box-marks', {
    shelfIndex: String(shelfIndex || '').trim(),
    storageIdPrefixes,
  });
  return validateResponse(
    AssignBatchResponseSchema,
    response.data,
    'POST /api/supabase/shelf-index/long-box-marks',
  );
};

/** Get SKU and shelf_index from shelf_indexes by full storage_id (UUID). */
export const getSkuByStorageId = async (storageId: string): Promise<{ sku: string; shelf_index: string } | null> => {
  try {
    const uuid = String(storageId || '').trim();
    if (!uuid) return null;
    const response = await axios.get('/api/supabase/shelf-index/sku-by-uuid', {
      params: { uuid }
    });
    if (response.status === 200 && response.data) {
      return response.data;
    }
    return null;
  } catch (error: unknown) {
    console.error('Error fetching SKU by storage ID:', error);
    return null;
  }
};

/** Get paginated browse indexes from shelf_indexes (checked_in = true). Returns { entries, hasMore } */
export const getBrowseIndexes = async (offset: number = 0, limit: number = 20): Promise<BrowseIndexesResponse> => {
  try {
    const response = await axios.get('/api/supabase/shelf-index/browse-indexes', {
      params: { offset, limit }
    });
    if (response.status === 200 && response.data) {
      return validateResponse(BrowseIndexesResponseSchema, response.data, 'getBrowseIndexes');
    }
    return { entries: [], hasMore: false };
  } catch (error: unknown) {
    console.error('Error fetching browse indexes:', error);
    return { entries: [], hasMore: false };
  }
};

/** Get paginated checked-out indexes from shelf_indexes (checked_in = false). Returns { entries, hasMore } with checked_out_at. */
export const getBrowseCheckedOutIndexes = async (offset: number = 0, limit: number = 20): Promise<BrowseCheckedOutIndexesResponse> => {
  try {
    const response = await axios.get('/api/supabase/shelf-index/browse-checked-out-indexes', {
      params: { offset, limit }
    });
    if (response.status === 200 && response.data) {
      return validateResponse(BrowseCheckedOutIndexesResponseSchema, response.data, 'getBrowseCheckedOutIndexes');
    }
    return { entries: [], hasMore: false };
  } catch (error: unknown) {
    console.error('Error fetching browse checked-out indexes:', error);
    return { entries: [], hasMore: false };
  }
};

/** Get label details by storage_id prefix (6 chars). For reprint flow. */
export const getLabelByStorageIdPrefix = async (storageIdPrefix: string): Promise<LabelLookup | null> => {
  try {
    const prefix = String(storageIdPrefix || '').trim().toLowerCase();
    if (prefix.length !== 6) return null;
    const response = await axios.get('/api/supabase/shelf-index/label-by-prefix', {
      params: { prefix }
    });
    if (response.status === 200 && response.data) {
      return validateResponse(LabelLookupSchema, response.data, 'getLabelByStorageIdPrefix');
    }
    return null;
  } catch (error: unknown) {
    console.error('Error fetching label by prefix:', error);
    return null;
  }
};

interface PrintAckResponseBody {
  message?: string;
  printed?: boolean;
  storageId?: string;
  storageIdPrefix?: string;
  error?: string;
}

function printAckFromResponse(
  data: PrintAckResponseBody,
  fallbackMessage: string,
): ApiMessageResult {
  const storageId = typeof data.storageId === 'string' ? data.storageId : undefined;
  const storageIdPrefix = typeof data.storageIdPrefix === 'string' ? data.storageIdPrefix : undefined;
  return {
    success: true,
    message: data.message || fallbackMessage,
    printed: data.printed !== false,
    ...(storageId ? { storageId } : {}),
    ...(storageIdPrefix ? { storageIdPrefix } : {}),
  };
}

async function postReprintLabel(
  endpoint: '/api/print-warehouse-label/reprint' | '/api/print-big-label/reprint',
  storageIdPrefix: string,
  copies: number,
  defaultFailMessage: string,
  timeoutMs?: number,
): Promise<ApiMessageResult> {
  try {
    const prefix = String(storageIdPrefix || '').trim().toLowerCase();
    if (prefix.length !== 6) {
      return { success: false, message: 'Storage ID prefix must be 6 characters' };
    }
    const response = await axios.post(
      endpoint,
      {
        storageIdPrefix: prefix,
        copies: copies || 1,
      },
      timeoutMs != null ? { timeout: timeoutMs } : undefined,
    );
    if (response.status === 200 && response.data?.success) {
      return printAckFromResponse(response.data, defaultFailMessage);
    }
    return { success: false, message: response.data?.error || defaultFailMessage };
  } catch (error: unknown) {
    console.error(`Error reprinting (${endpoint}):`, error);
    return {
      success: false,
      message: getApiErrorMessage(error, defaultFailMessage)
    };
  }
}

/** Reprint warehouse label (small) - no DB write. Pass 6-char storageIdPrefix (backend resolves label). copies 1-8, batched 4 per sheet. */
export const reprintWarehouseLabel = async (storageIdPrefix: string, copies: number = 1): Promise<ApiMessageResult> => {
  return postReprintLabel(
    '/api/print-warehouse-label/reprint',
    storageIdPrefix,
    copies,
    'Failed to reprint label'
  );
};

/** Reprint big label - no DB write. Pass 6-char storageIdPrefix (backend resolves label). copies default 1. */
export const reprintBigLabel = async (storageIdPrefix: string, copies: number = 1): Promise<ApiMessageResult> => {
  return postReprintLabel(
    '/api/print-big-label/reprint',
    storageIdPrefix,
    copies,
    'Failed to reprint big label',
    PRINT_ACK_TIMEOUT_MS,
  );
};

/** Reserve a storage_id for a new big label (no print, no shelf_indexes insert). */
export const reserveBigLabelStorageId = async (): Promise<ApiMessageResult> => {
  try {
    const response = await axios.post('/api/print-big-label/reserve', {}, { timeout: PRINT_ACK_TIMEOUT_MS });
    if (response.status === 200 && response.data?.success && typeof response.data.storageId === 'string') {
      const storageId = response.data.storageId;
      const storageIdPrefix =
        typeof response.data.storageIdPrefix === 'string'
          ? response.data.storageIdPrefix
          : storageId.substring(0, 6);
      return {
        success: true,
        message: response.data.message || 'Storage ID reserved',
        printed: true,
        storageId,
        storageIdPrefix,
      };
    }
    return { success: false, message: response.data?.error || 'Failed to reserve storage ID.' };
  } catch (error: unknown) {
    console.error('Error reserving big-label storage ID:', error);
    return { success: false, message: getApiErrorMessage(error, 'Failed to reserve storage ID.') };
  }
};


/** Set a shelf_indexes row as the primary pick location for a SKU. Unsets any previous primary. */
export const setPrimaryLocation = async (sku: string, storageIdPrefix: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axios.post('/api/supabase/shelf-index/set-primary', {
      sku: String(sku || '').trim(),
      storageIdPrefix: String(storageIdPrefix || '').trim().toLowerCase(),
    });
    if (response.status === 200 && response.data?.success) {
      return { success: true, message: response.data.message || 'Primary location updated' };
    }
    return { success: false, message: response.data?.error || 'Failed to set primary location' };
  } catch (error: unknown) {
    console.error('Error setting primary location:', error);
    return { success: false, message: getApiErrorMessage(error, 'Failed to set primary location') };
  }
};

export const submitShelfIndex = async (dataToSubmit: { shelfIndex: string; SKU: string; storageIdPrefix?: string; labelType?: string; baseQty?: number | null; isLongBox?: boolean }): Promise<string | undefined> => {
    try {
        const response = await axios.post('/api/shelf-index', dataToSubmit);
        if (response.status === 200) {
          return 'Shelf index submitted successfully!';
        }
    } catch (error: unknown) {
        console.error('Error submitting shelf index:', error);
        return 'Failed to submit shelf index.';
    }
};

export interface SubmitShelfIndexBatchParams {
  readonly shelfIndex: string;
  readonly storageIdPrefixes: readonly string[];
  readonly labelType?: LabelType;
  readonly baseQty?: number | null;
  readonly isLongBox?: boolean;
}

export const submitShelfIndexBatch = async (
  dataToSubmit: SubmitShelfIndexBatchParams
): Promise<AssignBatchResponse> => {
  const body: {
    shelfIndex: string;
    storageIdPrefixes: string[];
    labelType?: LabelType;
    baseQty?: number | null;
    isLongBox?: boolean;
  } = {
    shelfIndex: dataToSubmit.shelfIndex,
    storageIdPrefixes: [...dataToSubmit.storageIdPrefixes],
  };
  if (dataToSubmit.labelType) {
    body.labelType = dataToSubmit.labelType;
  }
  if (typeof dataToSubmit.baseQty === 'number') {
    body.baseQty = dataToSubmit.baseQty;
  }
  if (typeof dataToSubmit.isLongBox === 'boolean') {
    body.isLongBox = dataToSubmit.isLongBox;
  }
  try {
    const response = await axios.post('/api/shelf-index/batch', body);
    return validateResponse(AssignBatchResponseSchema, response.data, 'POST /api/shelf-index/batch');
  } catch (error: unknown) {
    return {
      success: false,
      assigned: 0,
      failed: [{ prefix: '', message: getApiErrorMessage(error, 'Failed to submit shelf index.') }],
    };
  }
};

export const printWarehouseLabel = async (
    sku: string,
    shelfIndex: string | null,
    labelType: LabelType,
    batch: Array<{ sku: string; shelfIndex?: string | null; labelType: LabelType }> | null = null,
    isBigLabel: boolean = false,
    copies: number = 1
): Promise<ApiMessageResult> => {
    try {
        const requestBody = batch && Array.isArray(batch) && batch.length > 0
            ? { batch }
            : { sku, shelfIndex: shelfIndex || null, labelType, isBigLabel, copies: copies || 1 };
        
        const response = await axios.post('/api/print-warehouse-label', requestBody);
        if (response.status === 200 && response.data.success) {
            return { success: true, message: response.data.message || 'Label printed successfully!' };
        }
        return { success: false, message: 'Failed to print label.' };
    } catch (error: unknown) {
        console.error('Error printing warehouse label:', error);
        return { success: false, message: getApiErrorMessage(error, 'Failed to print label.') };
    }
};

/** Print shelf labels - batch of shelf indices (e.g. 6-J-2). No SKU/DB entry. */
export const printShelfLabels = async (items: Array<{ shelfIndex: string }>): Promise<ApiMessageResult> => {
  try {
    if (!Array.isArray(items) || items.length === 0) {
      return { success: false, message: 'No items to print' };
    }
    const response = await axios.post('/api/print-shelf-labels', { batch: items });
    if (response.status === 200 && response.data.success) {
      return {
        success: true,
        message: response.data.message || 'Shelf labels printed.',
        printed: response.data.printed !== false
      };
    }
    return { success: false, message: response.data?.error || 'Failed to print shelf labels' };
  } catch (error: unknown) {
    console.error('Error printing shelf labels:', error);
    return { success: false, message: getApiErrorMessage(error, 'Failed to print shelf labels') };
  }
};

export const printBigLabel = async (
    sku: string,
    shelfIndex: string | null,
    labelType: LabelType,
    baseQty: string | number | null = null,
    copies: number = 1,
    storageId?: string | null,
): Promise<ApiMessageResult> => {
    try {
        const requestBody: {
          sku: string;
          shelfIndex: string | null;
          labelType: LabelType;
          baseQty: string | number | null;
          copies: number;
          storageId?: string;
        } = {
            sku,
            shelfIndex: shelfIndex || null,
            labelType,
            baseQty: baseQty || null,
            copies: copies || 1,
        };
        if (storageId) {
          requestBody.storageId = storageId;
        }

        const response = await axios.post('/api/print-big-label', requestBody, { timeout: PRINT_ACK_TIMEOUT_MS });

        if (response.status === 200 && response.data.success) {
            return printAckFromResponse(response.data, 'Big label printed successfully!');
        }
        return { success: false, message: response.data?.error || 'Failed to print big label.' };
    } catch (error: unknown) {
        console.error('Error printing big label:', error);
        return { success: false, message: getApiErrorMessage(error, 'Failed to print big label.') };
    }
};
